import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { emailsAPI } from '../api/emails.api';
import type {
  EmailUpdateRequest,
  EmailAssociateRequest,
  EmailAssignRequest,
  VoicemailTaskRequest
} from '../types/tracker.types';

/**
 * Hook to fetch emails for a specific affaire
 */
export const useTrackerEmails = (affaireId: number | null, includeDiscarded = false) => {
  return useQuery({
    queryKey: ['tracker-emails', affaireId, includeDiscarded],
    queryFn: async () => {
      if (!affaireId) {
        return { success: true, data: [], count: 0 };
      }
      return emailsAPI.getEmailsByAffaireId(affaireId, includeDiscarded);
    },
    enabled: affaireId !== null,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true
  });
};

/**
 * Hook to fetch unassigned emails
 */
export const useUnassignedEmails = (params?: {
  includeDiscarded?: boolean;
  discardedOnly?: boolean;
  triage_flag?: string;
  limit?: number;
  offset?: number;
}) => {
  return useQuery({
    queryKey: ['unassigned-emails', params],
    queryFn: () => emailsAPI.getUnassignedEmails(params),
    staleTime: 30000,
    refetchOnWindowFocus: true
  });
};

/**
 * Hook to fetch triage stats for filter badges
 */
export const useTriageStats = () => {
  return useQuery({
    queryKey: ['triage-stats'],
    queryFn: () => emailsAPI.getTriageStats(),
    staleTime: 30000,
    refetchOnWindowFocus: true
  });
};

/**
 * Hook to update email status
 */
export const useUpdateEmailStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, updates }: { emailId: string; updates: EmailUpdateRequest }) =>
      emailsAPI.updateEmailStatus(emailId, updates),
    onSuccess: () => {
      // Invalidate all email queries to refetch
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
    }
  });
};

/**
 * Hook to associate email to affaire
 */
export const useAssociateEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, data }: { emailId: string; data: EmailAssociateRequest }) =>
      emailsAPI.associateEmailToAffaire(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
    }
  });
};

/**
 * Hook to discard email
 */
export const useDiscardEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, reason }: { emailId: string; reason?: string }) =>
      emailsAPI.discardEmail(emailId, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
    }
  });
};

/**
 * Hook to assign email
 */
export const useAssignEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, data }: { emailId: string; data: EmailAssignRequest }) =>
      emailsAPI.assignEmail(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
    }
  });
};

/**
 * Hook to create a task from a voicemail email
 */
export const useCreateVoicemailTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, data }: { emailId: string; data: VoicemailTaskRequest }) =>
      emailsAPI.createVoicemailTask(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-data'] }); // Invalidate tracker to show new task
    }
  });
};

/**
 * Hook to reallocate email to a different affaire
 */
export const useReallocateEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, targetAffaireId }: { emailId: string; targetAffaireId: number }) =>
      emailsAPI.reallocateEmail(emailId, targetAffaireId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-data'] });
      queryClient.invalidateQueries({ queryKey: ['chronologic-data'] });
    }
  });
};

/**
 * Hook to assign email to multiple affaires (creates duplicates)
 */
export const useAssignToMultipleAffaires = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId, data }: { emailId: string; data: { affaire_ids: number[]; excluded_attachments?: string[] } }) =>
      emailsAPI.assignToMultipleAffaires(emailId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-data'] });
    }
  });
};

/**
 * Hook to get email duplicates
 */
export const useEmailDuplicates = (emailId: string | null) => {
  return useQuery({
    queryKey: ['email-duplicates', emailId],
    queryFn: async () => {
      if (!emailId) {
        return { success: true, data: [], count: 0 };
      }
      return emailsAPI.getEmailDuplicates(emailId);
    },
    enabled: emailId !== null,
    staleTime: 60000 // 1 minute
  });
};

/**
 * Hook to unlink email from affaire (goes back to unassigned)
 */
export const useUnlinkEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId }: { emailId: string }) =>
      emailsAPI.unlinkEmail(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-data'] });
      queryClient.invalidateQueries({ queryKey: ['chronologic-data'] });
    }
  });
};

/**
 * Hook to delete email copy
 */
export const useDeleteEmailCopy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ emailId }: { emailId: string }) =>
      emailsAPI.deleteEmailCopy(emailId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
      queryClient.invalidateQueries({ queryKey: ['unassigned-emails'] });
      queryClient.invalidateQueries({ queryKey: ['tracker-data'] });
      queryClient.invalidateQueries({ queryKey: ['chronologic-data'] });
    }
  });
};
